import { Component, Input, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { MyConfigOrganization } from '../../../../models/myConfig';
import { BackOffice24StatesService } from '../state/back-office24-states.service';
import { BackOfficeService } from '../../../../services/backoffice.service';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartData } from 'chart.js';
import moment from 'moment';

interface ParkingAreaData {
  parkingSiteName: string;
  parkingId: string;
  availableParkingSpots: number;
  activeParkingSpots: number;
  disabledParkingSpots: number;
  checkedInParkingSpots: number;
}

type ParkingData = ParkingAreaData[];

interface OrderData {
  orders: {
    [key: string]: number;
  };
}

interface ShuttleMetrics {
  totalBaggageArrival: number;
  totalBaggageDeparture: number;
  totalBaggageTotal: number;
  totalCarsArrival: number;
  totalCarsDeparture: number;
  totalCarsTotal: number;
  // totalChildseatsArrival: number;
  // totalChildseatsDeparture: number;
  // totalChildseatsTotal: number;
  totalFlightsArrival: number;
  totalFlightsDeparture: number;
  totalFlightsTotal: number;
  totalOrdersArrival: number;
  totalOrdersDeparture: number;
  totalOrdersTotal: number;
  totalPassengersArrival: number;
  totalPassengersDeparture: number;
  totalPassengersTotal: number;
}

interface ProcessedShuttleData {
  labels: string[];
  datasets: ShuttleMetrics[];
}

@Component({
  selector: 'app-reports',
  standalone: false,
  templateUrl: './reports.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './reports.component.css'
})
export class ReportsComponent implements OnInit, OnDestroy {
  @Input() organization: MyConfigOrganization;
  @ViewChild(BaseChartDirective) chart?: BaseChartDirective<'line'>;

  isLoading = false;
  timeAxisMode: 'days' | 'hours' = 'days';

  selectedStartDate: Date | null = null;
  selectedEndDate: Date | null = null;
  minShuttleDate: Date | null = null;
  maxShuttleDate: Date | null = null;

  parkingBarChartData: ChartData<'bar'> | null = null;
  parkingAreas: ParkingAreaData[] = [];
  selectedParkingArea: string = 'all';
  orderBarChartData: ChartData<'bar'> | null = null;
  shuttleLineChartData: ChartData<'line'> = { labels: [], datasets: [] };

  private shuttleData: any = null;
  private destroy$ = new Subject<void>();
  private orgSubscription = new Subscription();
  private activeRequests = 0;

  public readonly parkingBarChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    layout: {
      padding: {
        left: 15,
        right: 15,
        top: 15,
        bottom: 15
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        title: { display: true, text: 'Number of Spots' },
        grid: {
          display: true,
          color: 'rgba(0,0,0,0.1)'
        }
      },
      y: {
        title: { display: true, text: 'Parking Spot Categories' },
        grid: {
          display: false
        }
      }
    },
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: 'Parking Spots Dashboard',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: {
        enabled: true,
        mode: 'nearest',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        callbacks: {
          title: (context) => {
            return context[0].label;
          },
          label: (context) => {
            return `${context.parsed.x.toLocaleString()} spots`;
          }
        }
      }
    },
    elements: {
      bar: {
        borderWidth: 2,
        borderSkipped: false,
        borderRadius: 0
      }
    },
    onHover: (event, elements) => {
      if (event.native?.target) {
        (event.native.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'default';
      }
    }
  };

  public readonly orderBarChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    layout: {
      padding: {
        left: 15,
        right: 15,
        top: 15,
        bottom: 15
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        title: { display: true, text: 'Number of Orders' },
        grid: {
          display: true,
          color: 'rgba(0,0,0,0.1)'
        }
      },
      y: {
        title: { display: true, text: 'Order Sources' },
        grid: {
          display: false
        }
      }
    },
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: 'Orders by Source',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: {
        enabled: true,
        mode: 'nearest',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        callbacks: {
          title: (context) => {
            return context[0].label;
          },
          label: (context) => {
            return `${context.parsed.x.toLocaleString()} orders`;
          }
        }
      }
    },
    elements: {
      bar: {
        borderWidth: 2,
        borderSkipped: false,
        borderRadius: 0
      }
    },
    onHover: (event, elements) => {
      if (event.native?.target) {
        (event.native.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'default';
      }
    }
  };

  public readonly shuttleLineChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: { title: { display: true, text: 'Timeline' } },
      y: { beginAtZero: true, title: { display: true, text: 'Count' } }
    },
    plugins: {
      legend: { display: true, position: 'top' },
      title: {
        display: true, text: 'Shuttle Operations Timeline', font: {
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: { mode: 'index', intersect: false }
    }
  };

  constructor(
    private backOfficeService: BackOfficeService,
    private backOffice24StatesService: BackOffice24StatesService
  ) { }

  ngOnInit(): void {
    this.orgSubscription = this.backOffice24StatesService.currentOrganization$
      .pipe(takeUntil(this.destroy$))
      .subscribe(org => this.organization = org);

    this.setInitialsDates();
    this.loadAllData();
  }

  setInitialsDates(){
    const oneWeekMs = 24 * 60 * 60 * 1000 * 7;
    this.selectedStartDate = new Date();
    this.selectedEndDate = new Date(this.selectedStartDate.getTime() + oneWeekMs);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.orgSubscription.unsubscribe();
  }

  onDateRangeChange(): void {
    if (this.selectedStartDate && this.selectedEndDate) {
      const diffDays = moment(this.selectedEndDate).diff(moment(this.selectedStartDate), 'days');

      if (Math.abs(diffDays) < 1) {
        this.selectedEndDate = moment(this.selectedStartDate).add(1, 'day').toDate();
      }
      this.loadPlanningData();
    }
  }


  switchTimeAxis(event: any): void {
    this.timeAxisMode = event.value || this.timeAxisMode;
    this.processShuttleData();
  }

  private loadAllData(): void {
    this.loadParkingData();
    this.loadPlanningData();
    this.loadOrderDataFromAPI();
  }

  onParkingAreaChange(): void {
    this.updateParkingChartData();
  }

  private loadParkingData(): void {
    this.executeRequest(
      this.backOfficeService.getDashBoardParkingData(this.organization?.organizationId),
      (data: ParkingAreaData[]) => {
        this.parkingAreas = data;
        this.updateParkingChartData();
      }
    );
  }

  private updateParkingChartData(): void {
    if (!this.parkingAreas.length) {
      this.parkingBarChartData = null;
      return;
    }

    if (this.selectedParkingArea === 'all') {
      // Sum up all parking areas
      const combinedData = this.parkingAreas.reduce((acc, area) => ({
        parkingSiteName: 'All Parking Areas',
        parkingId: 'all',
        availableParkingSpots: acc.availableParkingSpots + (area.availableParkingSpots || 0),
        activeParkingSpots: acc.activeParkingSpots + (area.activeParkingSpots || 0),
        disabledParkingSpots: acc.disabledParkingSpots + (area.disabledParkingSpots || 0),
        checkedInParkingSpots: acc.checkedInParkingSpots + (area.checkedInParkingSpots || 0)
      }), {
        parkingSiteName: 'All Parking Areas',
        parkingId: 'all',
        availableParkingSpots: 0,
        activeParkingSpots: 0,
        disabledParkingSpots: 0,
        checkedInParkingSpots: 0
      });
      this.parkingBarChartData = this.createParkingChartData(combinedData);
    } else {
      // Show selected parking area
      const selectedArea = this.parkingAreas.find(area => area.parkingId === this.selectedParkingArea);
      if (selectedArea) {
        this.parkingBarChartData = this.createParkingChartData(selectedArea);
      }
    }
  }

  private loadPlanningData(): void {
    if(this.organization.config.handlesShuttles)
    {
      console.log('test');
      this.executeRequest(
        this.backOfficeService.getDashBoardPlanningData(
          this.organization?.organizationId,
          moment(this.selectedStartDate).format('YYYY-MM-DDTHH:mm:ss'),
          moment(this.selectedEndDate).format('YYYY-MM-DDTHH:mm:ss')),
        (data: any) => {
          this.shuttleData = data;
          const allDates: Date[] = [];
          data?.plannedShuttles?.orderShuttlePlanDays?.forEach((day: any) => {
            // const dayDate = new Date(day.dayDate + (day.dayDate.endsWith('Z') ? '' : 'Z'));
            allDates.push(new Date(day.dayDate));
            day.orderShuttlePlanHours.forEach((hour: any) => {
              // allDates.push(new Date(hour.fromDate + (hour.fromDate.endsWith('Z') ? '' : 'Z')));
              allDates.push(new Date(hour.fromDate));
            });
          });
          if (allDates.length) {
            this.minShuttleDate = new Date(Math.min(...allDates.map(d => d.getTime())));
            this.maxShuttleDate = new Date(Math.max(...allDates.map(d => d.getTime())));
            this.selectedStartDate = this.minShuttleDate;
            this.selectedEndDate = this.maxShuttleDate;
          }
          this.processShuttleData();
        }
      );
    }
  }
  private loadOrderDataFromAPI(): void {
    this.executeRequest(
      this.backOfficeService.getDashBoardOrderData(this.organization?.organizationId),
      (data: OrderData) => {
        if (data && data.orders) {
          this.orderBarChartData = this.createOrderChartData(data);
        }
      }
    );
  }

  private executeRequest<T>(request: any, onSuccess: (data: T) => void): void {
    this.incrementRequests();
    request.pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: T) => { onSuccess(data); this.decrementRequests(); },
        error: (error: any) => { console.error('API request failed:', error); this.decrementRequests(); }
      });
  }

  private createParkingChartData(data: ParkingAreaData): ChartData<'bar'> {
    return {
      labels: ['Available Spots', 'Active Spots', 'Disabled Spots', 'Checked In Spots'],
      datasets: [{
        label: data.parkingSiteName,
        data: [
          data.availableParkingSpots,
          data.activeParkingSpots,
          data.disabledParkingSpots,
          data.checkedInParkingSpots
        ],
        backgroundColor: [
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 99, 132, 0.8)',
          'rgba(75, 192, 192, 0.8)',
          'rgba(255, 206, 86, 0.8)'
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(255, 206, 86, 1)'
        ],
        borderWidth: 2,
        categoryPercentage: 0.95,
        barPercentage: 0.9,
        minBarLength: 5
      }]
    };
  }

  private createOrderChartData(data: OrderData): ChartData<'bar'> {
    const sortedEntries = Object.entries(data.orders).sort(([, a], [, b]) => (b as number) - (a as number));
    const labels = sortedEntries.map(([source]) => source);
    const values = sortedEntries.map(([, count]) => count as number);
    const colors = this.generateColors(labels.length);

    return {
      labels: labels,
      datasets: [{
        label: 'Order Count',
        data: values,
        backgroundColor: colors.map(c => c.background),
        borderColor: colors.map(c => c.border),
        borderWidth: 2,
        categoryPercentage: 0.95,
        barPercentage: 0.9,
        minBarLength: 5
      }]
    };
  }

  private generateColors(count: number) {
    const baseColors = [
      { background: 'rgba(54, 162, 235, 0.8)', border: 'rgba(54, 162, 235, 1)' },
      { background: 'rgba(255, 99, 132, 0.8)', border: 'rgba(255, 99, 132, 1)' },
      { background: 'rgba(75, 192, 192, 0.8)', border: 'rgba(75, 192, 192, 1)' },
      { background: 'rgba(255, 206, 86, 0.8)', border: 'rgba(255, 206, 86, 1)' }
    ];
    return Array.from({ length: count }, (_, i) => baseColors[i % baseColors.length]);
  }

  private processShuttleData(): void {
    if (!this.shuttleData?.plannedShuttles?.orderShuttlePlanDays) return;
    const processed = this.timeAxisMode === 'days'
      ? this.processByDays()
      : this.processByHours();
    this.shuttleLineChartData = this.createShuttleChartData(processed);
    this.updateChart();
  }

  private processByDays(): ProcessedShuttleData {
    const labels: string[] = [];
    const datasets: ShuttleMetrics[] = [];
    this.shuttleData.plannedShuttles.orderShuttlePlanDays.forEach((day: any) => {
      // const utcDate = new Date(day.dayDate + (day.dayDate.endsWith('Z') ? '' : 'Z'));
      if (this.isDateInRange(new Date(day.dayDate))) {
        const metrics = day.orderShuttlePlanHours.reduce((acc: any, hour: any) => {
          return {
            totalBaggageArrival: acc.totalBaggageArrival + (hour.orderShuttleArrival.totalBaggage || 0),
            totalBaggageDeparture: acc.totalBaggageDeparture + (hour.orderShuttleDeparture.totalBaggage || 0),
            totalBaggageTotal: acc.totalBaggageTotal + (hour.orderShuttleArrival.totalBaggage || 0) + (hour.orderShuttleDeparture.totalBaggage || 0),
            // totalChildseatsArrival: acc.totalChildseatsArrival + (hour.orderShuttleArrival.totalChildseats || 0),
            // totalChildseatsDeparture: acc.totalChildseatsDeparture + (hour.orderShuttleDeparture.totalChildseats || 0),
            // totalChildseatsTotal: acc.totalChildseatsTotal + (hour.orderShuttleArrival.totalChildseats || 0) + (hour.orderShuttleDeparture.totalChildseats || 0),
            totalCarsArrival: acc.totalCarsArrival + (hour.orderShuttleArrival.totalCars || 0),
            totalCarsDeparture: acc.totalCarsDeparture + (hour.orderShuttleDeparture.totalCars || 0),
            totalCarsTotal: acc.totalCarsTotal + (hour.orderShuttleArrival.totalCars || 0) + (hour.orderShuttleDeparture.totalCars || 0),
            totalFlightsArrival: acc.totalFlightsArrival + (hour.orderShuttleArrival.totalFlights || 0),
            totalFlightsDeparture: acc.totalFlightsDeparture + (hour.orderShuttleDeparture.totalFlights || 0),
            totalFlightsTotal: acc.totalFlightsTotal + (hour.orderShuttleArrival.totalFlights || 0) + (hour.orderShuttleDeparture.totalFlights || 0),
            totalOrdersArrival: acc.totalOrdersArrival + (hour.orderShuttleArrival.totalOrders || 0),
            totalOrdersDeparture: acc.totalOrdersDeparture + (hour.orderShuttleDeparture.totalOrders || 0),
            totalOrdersTotal: acc.totalOrdersTotal + (hour.orderShuttleArrival.totalOrders || 0) + (hour.orderShuttleDeparture.totalOrders || 0),
            totalPassengersArrival: acc.totalPassengersArrival + (hour.orderShuttleArrival.totalPassengers || 0),
            totalPassengersDeparture: acc.totalPassengersDeparture + (hour.orderShuttleDeparture.totalPassengers || 0),
            totalPassengersTotal: acc.totalPassengersTotal + (hour.orderShuttleArrival.totalPassengers || 0) + (hour.orderShuttleDeparture.totalPassengers || 0)
          };
        }, this.getEmptyMetrics());
        labels.push(new Date(day.dayDate).toLocaleDateString());
        datasets.push(metrics);
      }
    });
    return { labels, datasets };
  }

  private processByHours(): ProcessedShuttleData {
    const labels: string[] = [];
    const datasets: ShuttleMetrics[] = [];
    this.shuttleData.plannedShuttles.orderShuttlePlanDays.forEach((day: any) => {
      day.orderShuttlePlanHours.forEach((hour: any) => {
        // const utcDate = new Date(hour.fromDate + (hour.fromDate.endsWith('Z') ? '' : 'Z'));
        if (this.isDateInRange(new Date(hour.fromDate))) {
          labels.push(new Date(hour.fromDate).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }));
          datasets.push({
            totalBaggageArrival: hour.orderShuttleArrival.totalBaggage || 0,
            totalBaggageDeparture: hour.orderShuttleDeparture.totalBaggage || 0,
            totalBaggageTotal: (hour.orderShuttleArrival.totalBaggage || 0) + (hour.orderShuttleDeparture.totalBaggage || 0),
            totalCarsArrival: hour.orderShuttleArrival.totalCars || 0,
            totalCarsDeparture: hour.orderShuttleDeparture.totalCars || 0,
            totalCarsTotal: (hour.orderShuttleArrival.totalCars || 0) + (hour.orderShuttleDeparture.totalCars || 0),
            // totalChildseatsArrival: hour.orderShuttleArrival.totalChildseats || 0,
            // totalChildseatsDeparture: hour.orderShuttleDeparture.totalChildseats || 0,
            // totalChildseatsTotal: (hour.orderShuttleArrival.totalChildseats || 0) + (hour.orderShuttleDeparture.totalChildseats || 0),
            totalFlightsArrival: hour.orderShuttleArrival.totalFlights || 0,
            totalFlightsDeparture: hour.orderShuttleDeparture.totalFlights || 0,
            totalFlightsTotal: (hour.orderShuttleArrival.totalFlights || 0) + (hour.orderShuttleDeparture.totalFlights || 0),
            totalOrdersArrival: hour.orderShuttleArrival.totalOrders || 0,
            totalOrdersDeparture: hour.orderShuttleDeparture.totalOrders || 0,
            totalOrdersTotal: (hour.orderShuttleArrival.totalOrders || 0) + (hour.orderShuttleDeparture.totalOrders || 0),
            totalPassengersArrival: hour.orderShuttleArrival.totalPassengers || 0,
            totalPassengersDeparture: hour.orderShuttleDeparture.totalPassengers || 0,
            totalPassengersTotal: (hour.orderShuttleArrival.totalPassengers || 0) + (hour.orderShuttleDeparture.totalPassengers || 0)
          });
        }
      });
    });
    return { labels, datasets };
  }

  private isDateInRange(date: Date): boolean {
    if (!this.selectedStartDate || !this.selectedEndDate) return true;
    return date >= this.selectedStartDate && date <= this.selectedEndDate;
  }

  private createShuttleChartData(data: ProcessedShuttleData): ChartData<'line'> {
    const colors = {
      totalBaggageArrival: { border: 'rgb(255, 99, 132)', background: 'rgba(255, 99, 132, 0.2)' },
      totalBaggageDeparture: { border: 'rgb(255, 159, 164)', background: 'rgba(255, 159, 164, 0.2)' },
      totalBaggageTotal: { border: 'rgb(220, 53, 69)', background: 'rgba(220, 53, 69, 0.2)' },
      totalCarsArrival: { border: 'rgb(54, 162, 235)', background: 'rgba(54, 162, 235, 0.2)' },
      totalCarsDeparture: { border: 'rgb(116, 193, 255)', background: 'rgba(116, 193, 255, 0.2)' },
      totalCarsTotal: { border: 'rgb(13, 110, 253)', background: 'rgba(13, 110, 253, 0.2)' },
      // totalChildseatsArrival: { border: 'rgb(255, 205, 86)', background: 'rgba(255, 205, 86, 0.2)' },
      // totalChildseatsDeparture: { border: 'rgb(255, 225, 126)', background: 'rgba(255, 225, 126, 0.2)' },
      // totalChildseatsTotal: { border: 'rgb(255, 193, 7)', background: 'rgba(255, 193, 7, 0.2)' },
      totalFlightsArrival: { border: 'rgb(75, 192, 192)', background: 'rgba(75, 192, 192, 0.2)' },
      totalFlightsDeparture: { border: 'rgb(115, 212, 212)', background: 'rgba(115, 212, 212, 0.2)' },
      totalFlightsTotal: { border: 'rgb(32, 201, 151)', background: 'rgba(32, 201, 151, 0.2)' },
      totalOrdersArrival: { border: 'rgb(153, 102, 255)', background: 'rgba(153, 102, 255, 0.2)' },
      totalOrdersDeparture: { border: 'rgb(183, 142, 255)', background: 'rgba(183, 142, 255, 0.2)' },
      totalOrdersTotal: { border: 'rgb(102, 16, 242)', background: 'rgba(102, 16, 242, 0.2)' },
      totalPassengersArrival: { border: 'rgb(255, 159, 64)', background: 'rgba(255, 159, 64, 0.2)' },
      totalPassengersDeparture: { border: 'rgb(255, 189, 104)', background: 'rgba(255, 189, 104, 0.2)' },
      totalPassengersTotal: { border: 'rgb(253, 126, 20)', background: 'rgba(253, 126, 20, 0.2)' }
    };
    return {
      labels: data.labels,
      datasets: Object.entries(colors).map(([key, color]) => ({
        label: this.formatLabel(key),
        data: data.datasets.map(d => d[key as keyof ShuttleMetrics]),
        borderColor: color.border,
        backgroundColor: color.background,
        tension: 0.1
      }))
    };
  }

  private getEmptyMetrics(): ShuttleMetrics {
    return {
      totalBaggageArrival: 0,
      totalBaggageDeparture: 0,
      totalBaggageTotal: 0,
      totalCarsArrival: 0,
      totalCarsDeparture: 0,
      totalCarsTotal: 0,
      // totalChildseatsArrival: 0,
      // totalChildseatsDeparture: 0,
      // totalChildseatsTotal: 0,
      totalFlightsArrival: 0,
      totalFlightsDeparture: 0,
      totalFlightsTotal: 0,
      totalOrdersArrival: 0,
      totalOrdersDeparture: 0,
      totalOrdersTotal: 0,
      totalPassengersArrival: 0,
      totalPassengersDeparture: 0,
      totalPassengersTotal: 0
    };
  }

  private formatLabel(key: string): string {
    const labelMap: { [key: string]: string } = {
      totalBaggageArrival: 'Baggage (Arrival)',
      totalBaggageDeparture: 'Baggage (Departure)',
      totalBaggageTotal: 'Baggage (Total)',
      totalCarsArrival: 'Cars (Arrival)',
      totalCarsDeparture: 'Cars (Departure)',
      totalCarsTotal: 'Cars (Total)',
      // totalChildseatsArrival: 'Childseats (Arrival)',
      // totalChildseatsDeparture: 'Childseats (Departure)',
      // totalChildseatsTotal: 'Childseats (Total)',
      totalFlightsArrival: 'Flights (Arrival)',
      totalFlightsDeparture: 'Flights (Departure)',
      totalFlightsTotal: 'Flights (Total)',
      totalOrdersArrival: 'Orders (Arrival)',
      totalOrdersDeparture: 'Orders (Departure)',
      totalOrdersTotal: 'Orders (Total)',
      totalPassengersArrival: 'Passengers (Arrival)',
      totalPassengersDeparture: 'Passengers (Departure)',
      totalPassengersTotal: 'Passengers (Total)'
    };
    return labelMap[key] || key;
  }

  private updateChart(): void {
    this.chart?.update();
  }
  incrementRequests(): void {
    this.activeRequests++;
    if (this.activeRequests === 1) this.isLoading = true;
  }
  decrementRequests(): void {
    if (this.activeRequests > 0) this.activeRequests--;
    if (this.activeRequests === 0) this.isLoading = false;
  }
}
